import React, { Dispatch, SetStateAction } from 'react';
import { useQuery } from '@apollo/client';
import { QueryListResponse } from 'global';
import { blue, grey } from '@mui/material/colors';
import { Typography, Stack, Skeleton } from '@mui/material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';

import { userAdditionalAccountsApi } from '../api';
import { AccountRelationViewModel, AdditionalAccountsAPINameSpase } from '../api/types';
import moment from 'moment';
import { DateFormats } from '../../../../constants/common';
import { getVendorOrLenderContactRoleName } from '../../../../utils/formatters';

interface Props {
  currentAccountId: string;
  selectedRelation: string | null;
  onChose: Dispatch<SetStateAction<string | null>>;
}

export const ListOfAdditionalAccounts = ({ currentAccountId, selectedRelation, onChose }: Props) => {
  const { data, loading, error } = useQuery<
    AdditionalAccountsAPINameSpase<QueryListResponse<AccountRelationViewModel>>
  >(userAdditionalAccountsApi.getList);

  const additionalAccountOptions = data?.additionalAccount?.result?.list ?? [];

  return (
    <RenderWithFallback
      error={error}
      isLoading={loading}
      fallBack={
        <Stack direction="column" gap="0.5rem">
          {new Array(3).fill(null).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={42} width="100%" sx={{ borderRadius: '0.25rem' }} />
          ))}
        </Stack>
      }
    >
      <Stack direction="column" gap="0.5rem">
        {additionalAccountOptions.map(relation => {
          const isCurrentAccount = currentAccountId === relation.accountId;
          const isSelectedRelation = selectedRelation === relation.id;

          return (
            <Stack
              key={relation.id}
              justifyContent="space-between"
              sx={{
                padding: '0.5rem 1rem',
                borderRadius: '0.25rem',
                cursor: isCurrentAccount ? 'not-allowed' : 'pointer',
                border: `1px solid ${!isCurrentAccount ? grey[600] : blue[600]}`,
                boxShadow: ({ shadows }) => (isSelectedRelation ? shadows[1] : 'none'),

                ':hover': {
                  boxShadow: ({ shadows }) => (isCurrentAccount ? 'none' : shadows[1]),
                },
              }}
              onClick={() => {
                if (isCurrentAccount) return;
                onChose(isSelectedRelation ? null : relation.id);
              }}
            >
              <Stack direction="row" justifyContent="space-between">
                <Typography>{relation.accountName}</Typography>

                {isCurrentAccount && (
                  <Typography align="right" color="textSecondary">
                    Current
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption">
                  {[relation.accountType, getVendorOrLenderContactRoleName(relation.role), relation.locationName]
                    .filter(Boolean)
                    .join(', ')}
                </Typography>

                <Typography alignSelf="flex-end" variant="caption" color="textSecondary">
                  Access provided at: {moment(relation.createdAt).format(DateFormats.DateTimeMedium24Hour)}
                </Typography>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </RenderWithFallback>
  );
};
